import React from "react";
import clsx from "clsx";
import { observer } from "mobx-react-lite";
import { IconArrowsSort, IconChevronDown, IconPlus } from "@tabler/icons-react";
import SortMenu from "./SortMenu";
import { PropertyBrowserTreeNode } from "api-client";
import SimpleColumnsMenu from "./SimpleExistingMenu";
import { useTranslation } from "react-i18next";
import useOutsideClick from "hooks/useOutsideClick";
import { EMPTY_SORT_ROW } from "features/DynamicTable/consts";
import { dynamicTableStore } from "features/DynamicTable/DynamicTableStore";

export interface SortProps {
  id: string;
  property: PropertyBrowserTreeNode | undefined;
  sort: string;
}

export default observer(function SortByPropertyBrowser(props: {
  advancedMode?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const isEng = i18n.language === "en";
  const openIconRef = React.useRef<HTMLDivElement>(null);
  const selectedPropertiesKeys = new Set(
    dynamicTableStore.filterStore.selectedSortedByItems.map(
      (item) => item.property?.column_key
    )
  );

  const filteredItems = dynamicTableStore.visibleColumnProperties.filter(
    (item) => !selectedPropertiesKeys.has(item.column_key)
  );
  const closeView = () => dynamicTableStore.filterStore.setOpenSort(false);
  useOutsideClick(openIconRef, closeView);

  const handleOpenMenu = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.stopPropagation();
    dynamicTableStore.filterStore.setOpenSort(
      !dynamicTableStore.filterStore.openSort
    );
  };

  const addFirstSortRule = (value: PropertyBrowserTreeNode) => {
    dynamicTableStore.filterStore.setSelectedSortedByItems([EMPTY_SORT_ROW]);
    onChangeProperty(value, 0, true);
  };

  const onChangeProperty = (
    value: PropertyBrowserTreeNode,
    index: number,
    openMenu?: boolean
  ) => {
    const rows = [...dynamicTableStore.filterStore.selectedSortedByItems];
    rows[index].property = value;
    dynamicTableStore.filterStore.setSelectedSortedByItems([...rows]);
    openMenu && dynamicTableStore.filterStore.setOpenSort(true);
  };

  const clientRect = openIconRef.current?.getBoundingClientRect();

  function labelContent() {
    return (
      <div className="flex items-center">
        <IconArrowsSort width={17} className="mr-1" stroke={3} />
        <span className="mr-1">
          {dynamicTableStore.filterStore.validSortingRules?.length
            ? `${t("mappingEditor.sorted")} ${t("mappingEditor.by")}: `
            : t("mappingEditor.sort")}
        </span>
        {dynamicTableStore.filterStore.selectedSortedByItems
          .map((item) => {
            if (item.property)
              return isEng ? item.property?.name : item.property?.name_de;
          })
          .filter((rec) => rec)
          .join(", ")}
      </div>
    );
  }

  const hasSortingRules = Boolean(
    dynamicTableStore.filterStore.validSortingRules?.length
  );

  return (
    <div
      className="flex items-center"
      ref={openIconRef}
      onClick={handleOpenMenu}
    >
      {hasSortingRules && !props.advancedMode && labelContent()}
      <div className="overflow-visible">
        {hasSortingRules ? (
          <>
            <IconChevronDown
              className={clsx(
                "h-5 w-5 cursor-pointer",
                dynamicTableStore.filterStore.openSort ? "rotate-180" : ""
              )}
            />
            {dynamicTableStore.filterStore.openSort ? (
              <SortMenu
                cursorPosition={{
                  x: Number(clientRect?.x),
                  y: Number(clientRect?.y),
                }}
                items={filteredItems}
                onChangeProperty={onChangeProperty}
              />
            ) : null}
          </>
        ) : (
          <div className="relative">
            <SimpleColumnsMenu<PropertyBrowserTreeNode>
              isEng={isEng}
              handleSelect={(value) => addFirstSortRule(value)}
              items={filteredItems}
              placeholder={t("mappingEditor.findProperty")}
              icon={
                <>
                  {!props.advancedMode && labelContent()}
                  <IconPlus className="h-4 w-4 cursor-pointer" />
                </>
              }
              isOpen={(open: boolean) =>
                dynamicTableStore.filterStore.setOpenSort(open)
              }
            />
          </div>
        )}
      </div>
    </div>
  );
});
